"use client";
import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../FirebaseConfig.ts";
import { formatCurrencyPDF } from "../../utils/formatCurrencyPDF.js";
import { capitalize } from "../plataforma/plan-de-pagos/capitalize.js";
import { formatCurrency } from "../../utils/formatCurrency.js";
import { PDFDocument, rgb } from "pdf-lib";
import { planPagos } from "../../utils/planPagos.js";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import LayoutPlatform from "../plataforma/layout.js";

const CedulaVerification = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [cedula, setCedula] = useState("");
  const [requestData, setRequestData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Usuario mock para el layout de la plataforma
  const mockUser = {
    name: "Administrador",
    email: "admin@gemcorp.com",
  };

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cedula.trim()) {
      setError("Por favor ingresa tu número de cédula");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Query Firebase for the request with this cedula
      const q = query(
        collection(db, "requests"),
        where("identity", "==", cedula)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No se encontró ninguna solicitud con esta cédula");
        setRequestData(null);
        setLoading(false);
        return;
      }

      // Get the first matching request
      const data = {
        id: querySnapshot.docs[0].id,
        ...querySnapshot.docs[0].data(),
      };
      setRequestData(data);
    } catch (error) {
      console.error("Error checking cedula:", error);
      setError("Error al verificar la cédula. Intenta de nuevo.");
      setRequestData(null);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!requestData) return;

    setDownloadingPDF(true);
    let startY = 665;
    const rowHeight = 15;

    try {
      // Cargar el PDF de plantilla
      const pdfUrl = "/extracto_deudor.pdf";
      const existingPdfBytes = await fetch(pdfUrl).then((res) =>
        res.arrayBuffer()
      );

      // Cargar el PDF en pdf-lib
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      // Obtener la primera página y agregar texto
      const page = pdfDoc.getPage(0);
      let totalCapital = 0;
      let totalInteres = 0;
      let totalCuotas = 0;

      const { schedule } = planPagos(requestData);
      const filteredSchedule = schedule.filter((item) =>
        requestData.payments?.some(
          (paymentId) => String(paymentId) === String(item.cuota)
        )
      );

      const currentPayment =
        schedule[requestData.payments ? requestData.payments.length : 0] || {};

      const capital = currentPayment.capital || 0;
      const interes = currentPayment.interes || 0;
      const cuota = currentPayment.cuotaAPagar || 0;

      // Sumar totales
      totalCapital += capital;
      totalInteres += interes;
      totalCuotas += cuota;

      const nombre = capitalize(requestData.name || "");
      const cedulaValue = requestData.identity || "";
      const monto = formatCurrency(requestData.ammount || 0);
      const tasa = `${requestData.tasa || 0}%`;

      if (startY < 40) return;

      page.drawText(nombre.toUpperCase(), { x: 125, y: 691, size: 7.5 });
      page.drawText(cedulaValue, { x: 125, y: 673, size: 7.5 });
      page.drawText(`$${monto}`, { x: 145, y: 630, size: 7.5 });
      page.drawText(`$${formatCurrencyPDF(currentPayment?.cuotaAPagar)}`, {
        x: 145,
        y: 612,
        size: 7,
      });
      page.drawText(`${currentPayment?.fechaPago}`, {
        x: 145,
        y: 594,
        size: 7,
      });
      page.drawText(`$${formatCurrencyPDF(currentPayment?.cuotaAPagar)}`, {
        x: 145,
        y: 576,
        size: 7,
      });
      page.drawText(`${requestData.month}`, { x: 445, y: 629, size: 7.5 });
      page.drawText(`${requestData.payments?.length || 0}`, {
        x: 445,
        y: 612,
        size: 7.5,
      });
      page.drawText(
        `${
          Number(requestData.month) - Number(requestData.payments?.length || 0)
        }`,
        {
          x: 445,
          y: 594,
          size: 7.5,
        }
      );
      page.drawText(tasa, { x: 445, y: 576, size: 7.5 });

      startY -= rowHeight;

      page.drawText(`${dayjs().format("YYYY")}/${dayjs().format("MM")}/30`, {
        x: 380,
        y: 691,
        size: 8,
        color: rgb(0, 0, 0),
      });

      let startY2 = 495;
      filteredSchedule.forEach((el, index) => {
        if (startY2 < 40) return;
        page.drawText(`${el.fechaPago}`, { x: 115, y: startY2, size: 10 });
        page.drawText(`$${formatCurrencyPDF(Number(el.cuotaAPagar))}`, {
          x: 275,
          y: startY2,
          size: 10,
        });
        page.drawText(`$${formatCurrencyPDF(Number(el.saldoDeuda))}`, {
          x: 425,
          y: startY2,
          size: 10,
        });
        startY2 -= rowHeight;
      });

      // Guardar el PDF modificado como bytes
      const pdfBytes = await pdfDoc.save();

      // Crear un Blob con los bytes del PDF modificado
      const blob = new Blob([pdfBytes], { type: "application/pdf" });

      // Crear una URL para el Blob
      const url = URL.createObjectURL(blob);

      // Abrir una nueva pestaña con el PDF generado
      const newWindow = window.open(url, "_blank");
      setTimeout(() => {
        newWindow.document.title = "Reporte GEMCORP";
      }, 100);

      //       const url = URL.createObjectURL(blob);
      // const link = document.createElement("a");
      // link.href = url;
      // link.download = "Reporte_GEMCORP.pdf"; // 📝 nombre del archivo
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // URL.revokeObjectURL(url); // libera memoria
    } catch (error) {
      console.error("Error generating PDF:", error);
      setError("Error al generar el reporte. Intenta de nuevo.");
    } finally {
      setDownloadingPDF(false);
    }
  };

  const resetForm = () => {
    setRequestData(null);
    setCedula("");
    setError("");
  };

  const formatCurrencyDisplay = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        dot: "bg-yellow-400",
        label: "Pendiente",
      },
      aprobado: {
        bg: "bg-green-100",
        text: "text-green-800",
        dot: "bg-green-400",
        label: "Aprobado",
      },
      rechazado: {
        bg: "bg-red-100",
        text: "text-red-800",
        dot: "bg-red-400",
        label: "Rechazado",
      },
    };

    const config = statusConfig[status] || statusConfig.aprobado;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></div>
        {config.label}
      </span>
    );
  };

  // Contenido principal que se mostrará dentro o fuera del layout
  const MainContent = () => {
    const { schedule, totalCredit, totalInterest } = planPagos({
      ...requestData,
      tasa: requestData?.tasa,
      start_date: requestData?.start_date,
    });

    return (
      <div
        className={`${
          !requestData
            ? "min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4"
            : ""
        }`}
      >
        <div className={`${!requestData ? "max-w-4xl mx-auto" : ""}`}>
          {/* Header - Solo se muestra cuando NO hay requestData */}
          {!requestData && (
            <div
              className={`text-center mb-8 transition-all duration-700 ease-out ${
                isLoaded
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#49b0a8] rounded-full mb-4">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-white"
                >
                  <path
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Consulta tu Solicitud
              </h1>
              <p className="text-xl text-gray-600">
                Ingresa tu número de cédula para consultar el estado de tu
                solicitud
              </p>
            </div>
          )}

          {/* Header para cuando SÍ hay requestData (dentro del layout de plataforma) */}
          {requestData && (
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 ">
                    Detalles de Solicitud
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Información completa de su crédito
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div
            className={`transition-all duration-700 ease-out delay-200 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {!requestData ? (
                /* Search Form */
                <div className="p-8">
                  <div className="max-w-md mx-auto">
                    {/* Error Message */}
                    {error && (
                      <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="text-red-600"
                          >
                            <path
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <p className="text-red-800 font-medium">{error}</p>
                      </div>
                    )}

                    {/* Search Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="cedula"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Número de Cédula
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-4 0v2m0 0h4"
                              />
                            </svg>
                          </div>
                          <input
                            type="text"
                            id="cedula"
                            value={cedula}
                            onChange={(e) => setCedula(e.target.value)}
                            className="block w-full pl-12 pr-4 py-4 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                            placeholder="Ej: 1234567890"
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading || !cedula.trim()}
                        className={`
                        w-full group relative overflow-hidden px-6 py-4 rounded-xl font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:scale-95 flex items-center justify-center gap-2
                        ${
                          loading || !cedula.trim()
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-[#49b0a8] hover:bg-[#3a8a84] text-white"
                        }
                      `}
                      >
                        {!loading && cedula.trim() && (
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        )}

                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span className="relative z-10">
                              Verificando...
                            </span>
                          </>
                        ) : (
                          <>
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              className="relative z-10"
                            >
                              <path
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="relative z-10">
                              Consultar Solicitud
                            </span>
                          </>
                        )}
                      </button>
                    </form>

                    {/* Help Section */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="text-blue-600"
                          >
                            <path
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-blue-900 font-medium mb-1">
                            ¿Necesitas ayuda?
                          </h3>
                          <ul className="text-blue-800 text-sm space-y-1">
                            <li>
                              • Ingresa tu número de cédula sin puntos ni
                              espacios
                            </li>
                            <li>
                              • Asegúrate de que sea el mismo usado en la
                              solicitud
                            </li>
                            <li>
                              • Si no encuentras tu solicitud, contacta soporte
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Request Details */
                <div className="p-8">
                  {/* User Header */}
                  <div className="flex justify-center items-center gap-4 mb-8 p-6 bg-gradient-to-r from-[#49b0a8] to-[#5ebbaa] rounded-xl text-white">
                    <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-white font-bold text-2xl">
                      {requestData.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        {capitalize(requestData.name)}{" "}
                        {capitalize(requestData.lastName)}
                      </h2>
                      <p className="text-white/90">
                        Cédula: {requestData.identity}
                      </p>
                      <div className="mt-2">
                        {getStatusBadge(requestData?.status || "aprobado")}
                      </div>
                    </div>
                  </div>

                  {/* Credit Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
                      <div className="text-sm text-blue-600 mb-2 font-medium">
                        Valor del Crédito
                      </div>
                      <div className="text-2xl font-bold text-blue-900">
                        {formatCurrencyDisplay(
                          Number(requestData?.ammount) || 0
                        )}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center border border-green-200">
                      <div className="text-sm text-green-600 mb-2 font-medium">
                        Tasa de Interés
                      </div>
                      <div className="text-2xl font-bold text-green-900">
                        {requestData?.tasa || 0}%
                      </div>
                      <div className="text-xs text-green-600">Anual</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center border border-purple-200">
                      <div className="text-sm text-purple-600 mb-2 font-medium">
                        Plazo
                      </div>
                      <div className="text-2xl font-bold text-purple-900">
                        {requestData?.month || 0}
                      </div>
                      <div className="text-xs text-purple-600">Meses</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center border border-orange-200">
                      <div className="text-sm text-orange-600 mb-2 font-medium">
                        Cuota Mensual
                      </div>
                      <div className="text-2xl font-bold text-orange-900">
                        {formatCurrencyDisplay(
                          Math.round(schedule[0]?.cuotaAPagar)
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Payment Progress */}
                  {requestData?.payments && (
                    <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-[#49b0a8]"
                        >
                          <path
                            d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Progreso de Pagos
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-4 bg-white rounded-lg border">
                          <div className="text-sm text-gray-600 mb-1">
                            Cuotas Pagadas
                          </div>
                          <div className="text-2xl font-bold text-green-600">
                            {requestData.payments.length}
                          </div>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg border">
                          <div className="text-sm text-gray-600 mb-1">
                            Cuotas Pendientes
                          </div>
                          <div className="text-2xl font-bold text-orange-600">
                            {Number(requestData.month) -
                              requestData.payments.length}
                          </div>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg border">
                          <div className="text-sm text-gray-600 mb-1">
                            Progreso
                          </div>
                          <div className="text-2xl font-bold text-[#49b0a8]">
                            {Math.round(
                              (requestData.payments.length /
                                Number(requestData.month)) *
                                100
                            )}
                            %
                          </div>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-gradient-to-r from-[#49b0a8] to-[#5ebbaa] h-4 rounded-full transition-all duration-1000 ease-out"
                          style={{
                            width: `${
                              (requestData.payments.length /
                                Number(requestData.month)) *
                              100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Download Report Button */}
                  <div className="text-center">
                    <button
                      onClick={generatePDF}
                      disabled={downloadingPDF}
                      className={`
                      group relative overflow-hidden px-8 py-4 rounded-xl font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:scale-95 flex items-center justify-center gap-3 mx-auto
                      ${
                        downloadingPDF
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-900 hover:bg-gray-800 text-white"
                      }
                    `}
                    >
                      {!downloadingPDF && (
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      )}

                      {downloadingPDF ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span className="relative z-10">
                            Generando Reporte...
                          </span>
                        </>
                      ) : (
                        <>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="relative z-10"
                          >
                            <path
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="relative z-10 text-lg">
                            Descargar Reporte de Cobro
                          </span>
                        </>
                      )}
                    </button>

                    <p className="text-gray-600 text-sm mt-3">
                      El reporte incluye el detalle completo de tu crédito y
                      estado de pagos
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Info - Solo cuando NO hay requestData */}
          {!requestData && (
            <div
              className={`mt-8 text-center transition-all duration-700 ease-out delay-400 ${
                isLoaded
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  ¿No tienes una solicitud aún?
                </h3>
                <p className="text-gray-600 mb-4">
                  Solicita tu crédito de manera rápida y sencilla con nuestro
                  sistema en línea
                </p>
                <Link to={"/"}>
                  <button className="inline-flex items-center gap-2 bg-[#49b0a8] hover:bg-[#3a8a84] text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Solicitar Crédito
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderizado condicional: LayoutPlatform cuando hay requestData, layout normal cuando no
  if (requestData) {
    return (
      <LayoutPlatform employee user={mockUser}>
        <MainContent />
      </LayoutPlatform>
    );
  }

  // Layout normal para la búsqueda inicial
  return <MainContent />;
};

export default CedulaVerification;
